/**
*
*   Shopify Customer API Connection
*       - The Request Connection used for the Storefront data
*
**/

//The Endpoints
import Customer from './endpoints/customer';


/**
*
*   Shopify Customer API Class
*
*
**/
const CustomerAPI = (( token ) => {

	//Setup the Object
	const obj = {

		//Shopify Storefront Endpoint
		endpoint: process.env.NEXT_PUBLIC_SHOPIFY_CUSTOMER_DOMAIN,

		/**
		*
		*   Request
		*       - The Shopify Storefront Request function
		*
		*   Params:
		*       query:      (String) The GraphQL query to send
		*       variables:  (Object) The Variables to pass
		*
		*
		**/
		async Request( query , variables = {} ){
			try {

				//Prepare the Query
				query = obj[ query.type ]( query );

				//Send Request to Shopify
				const result = await fetch( obj.endpoint , {
					method:     'POST',
					headers:    {
						'Content-Type':     'application/json',
						'Authorization':    token
					},
					body:           { query, variables } && JSON.stringify({ query, variables })
				});

				//Return Response if Successful
				return {
					status:     result.status,
					body:       await result.json()
				};

			} catch(error){

				//Return Error
				return {
					status: 500,
					errors:  [
						error
					]
				};
			}

		},

		/**
		*
		*   prepare the query
		*       - Sets up the query localization
		*
		*   Params:
		*       item:    (String) The String to Parse
		*
		*
		**/
		query: ({ variables, request }) => {
			// @inContext(country:${ Config.country }, language:${ locale.toUpperCase() }){
			return `
				query${ ( variables ? `(${  variables  })` : '' ) } {
					${ request }
				}
			`;
		},



		/**
		*
		*   prepare the query
		*       - Sets up the query localization
		*
		*   Params:
		*       item:    (String) The String to Parse
		*
		*
		**/
		mutation: ({ name, variables, request }) => {
			//@inContext(country:${ Config.country }, language:${ Storefront.get('locale').toUpperCase() })
			return `
				mutation ${ name }${ ( variables ? `(${  variables  })` : '' ) } {
					${ request }
				}
			`;
		},



		/**
		*
		*   error
		*       - Error Response
		*
		*   Params:
		*       item:    (String) The String to Parse
		*
		*
		**/
		error: (data) => {
			return {
				result: 0,
				errors:  data
			};
		},



		/**
		*
		*   success
		*       - Success
		*
		*   Params:
		*       item:    (String) The String to Parse
		*
		*
		**/
		success: (data) => {
			return {
				result: 1,
				body:   data
			};
		}

	};


	//Return
	return {

		/**
		*
		*   Customer.get
		*       - Loads customer details based on their token
		*
		*   Params:
		*       token:     (String) The Customer Token
		*
		**/
		get: async () => {

			//Get the Response
			const response = await obj.Request( Customer.get , {});

			//Return Fail
			if( response.status != 200 ) return obj.error( response.body.errors );
			if( !response.body?.data?.customer ) return obj.success( false );

			//Return the Customer
			return obj.success( Customer.format( obj , response.body.data.customer ) );

		},


		/**
		*
		*   Customer.update
		*       - Updates a customer
		*
		*   Params:
		*       query:     (String) The Search Query
		*
		**/
		update: async input => {

			//Get the Response
			const response = await obj.Request( Customer.update , { input: input });

			//Failed Response
			if( response.status != 200 ) return obj.error( response.body.errors );
			if( response.body?.errors?.length > 0 ) return obj.error( response.body.errors );
			if( response.body.data.customerUpdate?.customerUserErrors?.length > 0 ) return obj.error( response.body.data.customerUpdate.customerUserErrors );

			//Return the Address
			return obj.success( Customer.format( obj , response.body?.data?.customerUpdate?.customer ) );

		},



		addresses: {

			/**
			*
			*   Customer.addresses.get
			*       - Loads customer addresses
			*
			*   Params:
			*       token:     (String) The Customer Token
			*
			**/
			list: async () => {

				//Get the Response
				const response = await obj.Request( Customer.addresses.list , {});

				//Return Fail
				if( response.status != 200 ) return obj.error( response.body.errors );

				//Return the Customer
				return obj.success( Customer.format( obj , response.body.data.customer ) );

			},

			/**
			*
			*   Customer.addresses.update
			*       - Update an existing address
			*
			*   Params:
			*
			**/
			create: async ( address , isDefault = false ) => {

				//Get the Response
				const response = await obj.Request( Customer.addresses.create , { address: address , default: isDefault });

				//Return Fail
				if( response.status != 200 ) return obj.error( ( response?.errors || response.body?.errors ) );
				if( response?.body?.errors?.length > 0 ) return obj.error( response.body.errors );
				if( response.body?.data?.customerAddressCreate?.customerUserErrors?.length > 0 ) return obj.error( response.body.data.customerAddressCreate.customerUserErrors );

				//Return the Address
				return obj.success( response.body?.data?.customerAddressCreate.customerAddress );

			},

			/**
			*
			*   Customer.addresses.update
			*       - Update an existing address
			*
			*   Params:
			*
			**/
			update: async ( id , input , isDefault = false ) => {

				//Get the Response
				const response = await obj.Request( Customer.addresses.update , { id: id , address: input , default: isDefault });

				//Return Fail
				if( response.status != 200 ) return obj.error( ( response?.errors || response.body?.errors ) );
				if( response?.body?.errors?.length > 0 ) return obj.error( response.body.errors );
				if( response.body.data?.customerAddressUpdate?.customerUserErrors?.length > 0 ) return obj.error( response.body.data.customerAddressUpdate.customerUserErrors );

				//Return the Address
				return obj.success( response.body?.data?.address );

			},

			/**
			*
			*   Customer.addresses.delete
			*       - Delete an existing address
			*
			*   Params:
			*       token:     (String) The Customer Token
			*
			**/
			delete: async ( id ) => {

				//Get the Response
				let response = await obj.Request( Customer.addresses.delete , { id: id });

				//Return Fail
				if( response.status != 200 ) return obj.error( response.body.errors );
				if( response?.body?.errors?.length > 0 ) return obj.error( response.body.errors );
				if( response?.body?.data?.customerAddressDelete?.customerUserErrors?.length > 0 ) return obj.error( response.body.data.customerAddressDelete.customerUserErrors );

				//Get The Response
				response = response.body?.data?.customerAddressDelete;

				//Return the Address
				return obj.success( response );

			}

		},



		/**
		*
		*   passowrd
		*       - Password Queries
		*
		*
		**/
		order: {

			/**
			*
			*   Customer.order.history
			*       - Get a customers Order History
			*       - ** DOES NOT WORK WITH SHOPIFY STOREFRONT **
			*
			*   Params:
			*       data:     (String) The Customer data
			*
			**/
			get: async orderid => {

				//Get the Response
				const response = await obj.Request( Customer.order.get , {
					query: `name:#${  orderid }`
				});

				//Return Fail
				if( response.status != 200 ) return obj.error( response.body.errors );
				if( response.body?.errors?.length >= 1 ) return obj.error( response.body.errors );

				//Return the Customer
				return obj.success(
					Customer.format( obj , response.body.data.customer )
				);

			},



			/**
			*
			*   Customer.order.history
			*       - Get a customers Order History
			*
			*   Params:
			*       data:     (String) The Customer data
			*
			**/
			history: async () => {

				//Get the Response
				const response = await obj.Request( Customer.order.history );

				//Return Fail
				if( response.status != 200 ) return obj.error( response.body.errors );
				if( response.body?.errors?.length >= 1 ) return obj.error( response.body.errors );

				//Return the Customer
				return obj.success(
					Customer.format( obj , response.body.data.customer )
				);

			}


		}

	};

} );


//Output
export default CustomerAPI;